<template>
    <main>
        <modalInformacionUsuario :usuario="usuario"></modalInformacionUsuario>
        <page-header-compact>
            Partograma
            <template v-slot:button>
                <a v-if="partograma.id" :href="urlImpresion" target="_blank"
                    class="btn btn-primary btn-sm shadow-sm mx-2">
                    Imprimir partograma <i class="fas fa-print fa-fw"></i>
                </a>
                <router-link :to="{ name: 'historias.perfil.usuario', params: { idUsuario: usuario.id } }"
                    class="btn btn-success btn-sm shadow-sm mx-2">
                    Regresar al perfil del paciente <i class="fas fa-history fa-fw"></i>
                </router-link>
            </template>
        </page-header-compact>

        <div class="container-xl px-4">
            <div class="card mt-2">
                <cardInformacionUsuario :usuario="usuario"></cardInformacionUsuario>
            </div>

            <!-- Lo primero que debe ver quien recibe el turno. -->
            <div v-if="alertas.length" class="card mt-2 border-left-lg"
                 :class="hayAltas ? 'border-left-danger' : 'border-left-warning'">
                <div class="card-body py-2">
                    <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-exclamation-triangle fa-fw mr-2"
                           :class="hayAltas ? 'text-danger' : 'text-warning'"></i>
                        <strong>{{ alertas.length }} {{ alertas.length === 1 ? 'alerta' : 'alertas' }}</strong>
                    </div>
                    <div v-for="(a, i) in alertas" :key="i" class="small mb-1">
                        <span class="badge mr-1" :class="a.nivel === 'ALTA' ? 'badge-danger' : 'badge-warning'">
                            {{ a.nivel }}
                        </span>
                        <strong>{{ a.titulo }}</strong>
                        <span class="text-muted" v-if="a.fecha"> · {{ a.fecha | formatDateTime }}</span>
                        <div class="text-muted ml-4">{{ a.detalle }}</div>
                    </div>
                </div>
            </div>

            <div class="row gutters-sm">
                <div class="col-md-4 mb-3">
                    <div class="card mt-2">
                        <div class="card-header py-2 d-flex justify-content-between align-items-center">
                            <span>Trabajo de parto</span>
                            <span :class="['badge', partograma.estado === 'ABIERTO' ? 'badge-success' : 'badge-secondary']">
                                {{ partograma.estado }}
                            </span>
                        </div>
                        <div class="card-body small">
                            <div><strong>Inicio:</strong> {{ partograma.fecha_inicio | formatDateTime }}</div>
                            <div><strong>Paridad:</strong> {{ partograma.paridad }}</div>
                            <div><strong>Membranas al inicio:</strong> {{ partograma.membranas_inicio }}</div>
                            <div><strong>Dilatación inicial:</strong> {{ partograma.dilatacion_inicio }} cm</div>
                            <div v-if="partograma.desenlace"><strong>Desenlace:</strong> {{ partograma.desenlace }}</div>

                            <hr class="my-2">

                            <label class="small font-weight-bolder mb-1">Patrón de la curva de alerta</label>
                            <div class="list-group list-group-flush border rounded mb-2">
                                <button v-for="item in dataPartograma" :key="item.id" type="button"
                                        class="list-group-item list-group-item-action py-2 px-2"
                                        :class="{'active': patron && patron.id === item.id}"
                                        @click="patron = item">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <span style="font-size: 12px;">
                                            <i class="fas fa-fw" :class="patron && patron.id === item.id ? 'fa-check-circle' : 'fa-circle-o'"></i>
                                            {{ item.paridad === 'TODAS' ? 'Todas las paridades' : (item.paridad === 'NULIPARAS' ? 'Nulípara' : 'Multípara') }}
                                            · {{ item.posicion === 'VERTICAL' ? 'Vertical' : 'Horizontal' }}
                                        </span>
                                        <span class="badge" :class="item.membranas === 'ROTAS' ? 'badge-warning' : 'badge-light'">
                                            {{ item.membranas === 'ROTAS' ? 'Rotas' : 'Íntegras' }}
                                        </span>
                                    </div>
                                </button>
                            </div>
                            <p class="text-muted" style="font-size: 11px;">
                                Se marca solo con la paridad y las membranas registradas. Si las membranas se rompen,
                                el CLAP indica reconstruir la curva; puede cambiar el patrón aquí.
                            </p>

                            <button v-if="abierto" class="btn btn-info btn-sm btn-block" data-toggle="modal"
                                    data-target="#modalPartograma">
                                <i class="fas fa-plus fa-fw"></i> Registrar control
                            </button>
                            <button v-if="abierto" class="btn btn-outline-secondary btn-sm btn-block" @click="cerrar()">
                                <i class="fas fa-flag-checkered fa-fw"></i> Cerrar partograma
                            </button>

                            <modal v-if="partograma.id" :idPartograma="partograma.id" @guardado="recargar()"></modal>
                        </div>
                    </div>
                </div>

                <div class="col-md-8">
                    <div class="card mt-2">
                        <div class="card-header py-2">Curva de dilatación</div>
                        <div class="card-body">
                            <chart :patron="patron" :controles="controles"></chart>
                        </div>
                    </div>

                    <div class="card mt-2">
                        <div class="card-header py-2">Controles ({{ controles.length }})</div>
                        <div class="card-body p-0">
                            <div class="table-responsive">
                                <table class="table table-sm table-striped mb-0" style="font-size: 12px;">
                                    <thead class="bg-light">
                                        <tr>
                                            <th>Hora</th>
                                            <th class="text-center">Dilatación</th>
                                            <th class="text-center">Descenso</th>
                                            <th class="text-center">FCF</th>
                                            <th class="text-center">TA</th>
                                            <th class="text-center">Pulso</th>
                                            <th class="text-center">Contracciones</th>
                                            <th class="text-center">Membranas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="c in controles" :key="c.id">
                                            <td>{{ c.fecha | formatDateTime }}</td>
                                            <td class="text-center">{{ c.dilatacion }} cm</td>
                                            <td class="text-center">{{ c.descenso || '—' }}</td>
                                            <td class="text-center">{{ c.frecuencia_fetal || '—' }}</td>
                                            <td class="text-center">
                                                <span v-if="c.ta_sistolica">{{ c.ta_sistolica }}/{{ c.ta_diastolica }}</span>
                                                <span v-else>—</span>
                                            </td>
                                            <td class="text-center">{{ c.pulso || '—' }}</td>
                                            <td class="text-center">
                                                <span v-if="c.frecuencia_contracciones">
                                                    {{ c.frecuencia_contracciones }} / 10 min · {{ c.duracion_contracciones }} s
                                                </span>
                                                <span v-else>—</span>
                                            </td>
                                            <td class="text-center">
                                                {{ c.membranas || '—' }}
                                                <small v-if="c.liquido_amniotico" class="text-muted d-block">{{ c.liquido_amniotico }}</small>
                                            </td>
                                        </tr>
                                        <tr v-if="!controles.length">
                                            <td colspan="8" class="text-center text-muted py-3">Sin controles registrados</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import PageHeaderCompact from "../../../../components/layouts/content/page-header-compact";
import modalInformacionUsuario from "../../base/informacionUsuario/modalInformacionUsuario";
import cardInformacionUsuario from "../../base/informacionUsuario/cardInformacionUsuario";
import usuarioService from '../../../../services/usuarioService';
import Toast from '../../../../components/common/utilities/toast';
import Swal from 'sweetalert2';
import partogramaJson from '../../../../assets/datasheets/charts/partograma.json';
import chart from "./chart.vue";
import modal from "./modal.vue";
import partogramaService from "../../../../services/partogramaService";

export default {
    components: { PageHeaderCompact, modalInformacionUsuario, cardInformacionUsuario, chart, modal },
    data() {
        return {
            usuario: {},
            partograma: {},
            controles: [],
            alertas: [],
            dataPartograma: partogramaJson,
            patron: null,
        };
    },
    computed: {
        abierto() {
            return this.partograma.estado === 'ABIERTO';
        },
        urlImpresion() {
            return partogramaService.print(this.partograma.id);
        },
        hayAltas() {
            return this.alertas.some(a => a.nivel === 'ALTA');
        },
    },
    methods: {
        async cargarUsuario(id) {
            const { data } = await usuarioService.show(id);
            this.usuario = data;
        },
        async cargarPartograma(id) {
            const { data } = await partogramaService.show(id);
            this.partograma = data;
            this.controles = data.controles || [];
            this.alertas = data.alertas || [];
            this.seleccionarPatron();
        },
        /** Patrón CLAP según paridad y membranas; las membranas se toman del último control. */
        seleccionarPatron() {
            const ultimo = this.controles[this.controles.length - 1];
            const membranas = (ultimo && ultimo.membranas) || this.partograma.membranas_inicio || 'INTEGRAS';
            const paridad = this.partograma.paridad === 'NULIPARA' ? 'NULIPARAS' : 'MULTIPARAS';
            const posicion = (ultimo && ultimo.posicion) || this.partograma.posicion_inicio || 'HORIZONTAL';

            this.patron = this.dataPartograma.find(p =>
                p.posicion === posicion && p.paridad === paridad && p.membranas === membranas
            ) || this.dataPartograma.find(p => p.posicion === posicion) || this.dataPartograma[0];
        },
        async recargar() {
            await this.cargarPartograma(this.$route.params.id);
        },
        async cerrar() {
            const { value: desenlace } = await Swal.fire({
                title: 'Cerrar partograma',
                input: 'select',
                inputOptions: {
                    'Parto vaginal': 'Parto vaginal',
                    'Cesárea': 'Cesárea',
                    'Remisión': 'Remisión',
                    'Otro': 'Otro',
                },
                inputPlaceholder: 'Desenlace',
                showCancelButton: true,
                confirmButtonText: 'Cerrar partograma',
                cancelButtonText: 'Cancelar',
            });
            if (!desenlace) return;

            try {
                this.LoaderSpinnerShow();
                await partogramaService.close(this.partograma.id, { desenlace });
                await this.recargar();
                Toast.fire({ icon: 'success', title: 'Partograma cerrado' });
            } catch (e) {
                Toast.fire({ icon: 'error', title: 'No se pudo cerrar el partograma' });
            } finally {
                this.LoaderSpinnerHide();
            }
        },
    },
    async created() {
        try {
            this.LoaderSpinnerShow();
            await this.cargarPartograma(this.$route.params.id);
            await this.cargarUsuario(this.partograma.id_usuario);
        } catch (error) {
            console.error(error);
            Toast.fire({ icon: 'error', title: 'Ocurrió un error al cargar el partograma' });
        } finally {
            this.LoaderSpinnerHide();
        }
    },
};
</script>
